import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

export default function NewJobDialog({ open, onOpenChange, onCreated, initial = null }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    project_name: "",
    client: "",
    site_address: "",
    manager: "",
    start_date: new Date().toISOString().slice(0, 10),
    status: "active",
  });
  const [operators, setOperators] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/users").then(({ data }) => {
      setOperators(data.filter((u) => u.role === "operatore"));
    }).catch(() => setOperators([]));
    if (initial) {
      setForm({
        project_name: initial.project_name || "",
        client: initial.client || "",
        site_address: initial.site_address || "",
        manager: initial.manager || "",
        start_date: initial.start_date || new Date().toISOString().slice(0, 10),
        status: initial.status || "active",
      });
      setAssigned(initial.assigned_operator_ids || []);
    } else {
      setForm({
        project_name: "",
        client: "",
        site_address: "",
        manager: "",
        start_date: new Date().toISOString().slice(0, 10),
        status: "active",
      });
      setAssigned([]);
    }
    setError("");
  }, [open, initial]);

  const toggleOp = (id) => {
    setAssigned((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form, assigned_operator_ids: assigned };
      let data;
      if (isEdit) {
        ({ data } = await api.patch(`/jobs/${initial.id}`, payload));
      } else {
        ({ data } = await api.post("/jobs", payload));
      }
      onCreated?.(data);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-job-dialog">
        <DialogHeader>
          <DialogTitle className="text-brand-navy">
            {isEdit ? "Modifica lavoro" : "Nuovo lavoro"}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? "Aggiorna i dati del cantiere." : "Inserisci i dati del nuovo cantiere."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="project_name">Nome progetto</Label>
            <Input id="project_name" required value={form.project_name}
              onChange={(e) => setForm({ ...form, project_name: e.target.value })}
              data-testid="job-project-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="client">Cliente</Label>
              <Input id="client" required value={form.client}
                onChange={(e) => setForm({ ...form, client: e.target.value })}
                data-testid="job-client-input" />
            </div>
            <div>
              <Label htmlFor="manager">Responsabile</Label>
              <Input id="manager" required value={form.manager}
                onChange={(e) => setForm({ ...form, manager: e.target.value })}
                data-testid="job-manager-input" />
            </div>
          </div>
          <div>
            <Label htmlFor="site_address">Indirizzo cantiere</Label>
            <Input id="site_address" required value={form.site_address}
              onChange={(e) => setForm({ ...form, site_address: e.target.value })}
              data-testid="job-address-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="start_date">Data inizio</Label>
              <Input id="start_date" type="date" required value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                data-testid="job-start-date-input" />
            </div>
            {isEdit && (
              <div>
                <Label>Stato</Label>
                <div className="flex gap-2 mt-1.5">
                  <Button type="button"
                    variant={form.status === "active" ? "default" : "outline"}
                    className={form.status === "active" ? "brand-navy text-white" : ""}
                    onClick={() => setForm({ ...form, status: "active" })}
                    data-testid="job-status-active-button">
                    Attivo
                  </Button>
                  <Button type="button"
                    variant={form.status === "completed" ? "default" : "outline"}
                    className={form.status === "completed" ? "brand-navy text-white" : ""}
                    onClick={() => setForm({ ...form, status: "completed" })}
                    data-testid="job-status-completed-button">
                    Concluso
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div>
            <Label>Operatori assegnati</Label>
            <div className="mt-2 border rounded-lg divide-y max-h-40 overflow-y-auto">
              {operators.length === 0 && (
                <div className="p-3 text-sm text-slate-500">Nessun operatore disponibile.</div>
              )}
              {operators.map((op) => (
                <label key={op.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50">
                  <Checkbox
                    checked={assigned.includes(op.id)}
                    onCheckedChange={() => toggleOp(op.id)}
                    data-testid={`job-operator-checkbox-${op.id}`}
                  />
                  <div className="text-sm">
                    <div className="font-medium text-slate-800">{op.name}</div>
                    <div className="text-xs text-slate-500">{op.email}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {error && <div className="text-sm text-red-600" data-testid="new-job-error">{error}</div>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="new-job-cancel-button">
              Annulla
            </Button>
            <Button type="submit" disabled={saving} className="brand-navy text-white hover:opacity-90"
              data-testid="new-job-submit-button">
              {saving ? "Salvataggio..." : isEdit ? "Salva modifiche" : "Crea lavoro"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
